import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { lastValueFrom } from 'rxjs';
import { AppConfigService } from '../../auth-guard/appconfig.service';

const GRAPH_SCOPES = ['https://graph.microsoft.com/.default'];

@Injectable({
    providedIn: 'root'
})
export class GraphService {
    constructor(
        private http: HttpClient,
        private readonly msalService: MsalService,
        private readonly config: AppConfigService
    ) { }

    private async getGraphToken(): Promise<string> {
        if (this.isRunningInIframe()) {
            const cachedToken = this.getValidStoredToken();
            if (cachedToken) {
                return cachedToken;
            }

            throw new Error('[GraphService] Silent token acquisition skipped in iframe context.');
        }

        const account = this.msalService.instance.getActiveAccount();
        if (!account) {
            throw new Error('[GraphService] No active MSAL account. User must be logged in.');
        }
        let tokenResponse: AuthenticationResult;

        try {
            tokenResponse = await lastValueFrom(
                this.msalService.acquireTokenSilent({
                    scopes: GRAPH_SCOPES,
                    account: account
                })
            );
        } catch (error: any) {
            if (error?.errorCode === 'block_iframe_reload') {
                const cachedToken = this.getValidStoredToken();
                if (cachedToken) {
                    return cachedToken;
                }
            }

            throw error;
        }

        // Cache Graph token for iframe contexts where MSAL silent acquisition may be blocked
        sessionStorage.setItem('graph_access_token', tokenResponse.accessToken);
        if (tokenResponse.expiresOn) {
            sessionStorage.setItem('graph_access_token_expires_on', tokenResponse.expiresOn.toISOString());
        } else {
            sessionStorage.removeItem('graph_access_token_expires_on');
        }

        return tokenResponse.accessToken;
    }

    private isRunningInIframe(): boolean {
        return typeof window !== 'undefined' && window.self !== window.top;
    }

    private getValidStoredToken(): string | null {
        const token = sessionStorage.getItem('graph_access_token');
        const expiresOn = sessionStorage.getItem('graph_access_token_expires_on');

        if (!token || (expiresOn && new Date(expiresOn).getTime() <= Date.now())) {
            sessionStorage.removeItem('graph_access_token');
            sessionStorage.removeItem('graph_access_token_expires_on');
            return null;
        }

        if (expiresOn && new Date(expiresOn).getTime() <= Date.now()) {
            return null;
        }

        return token;
    }

    private async fetchAllGraphPages(url: string, headers: HttpHeaders): Promise<any[]> {
        const allItems: any[] = [];
        let nextUrl = url;

        while (nextUrl) {
            const page: any = await lastValueFrom(this.http.get(nextUrl, { headers }));
            const pageItems = Array.isArray(page?.value) ? page.value : [];
            allItems.push(...pageItems);
            nextUrl = page?.['@odata.nextLink'] || '';
        }

        return allItems;
    }

    public async getUserPhoto(): Promise<any> {
        const p = new Promise<any>((resolve, reject) => {
            this.getGraphToken().then(graphToken => {
                let url = "https://graph.microsoft.com/v1.0/me/photo/$value";
                let headers: HttpHeaders = new HttpHeaders({
                    Authorization: `Bearer ${graphToken}`
                });

                let stream$ = this.http.get(url, { headers: headers, responseType: "blob" });

                if (stream$) {
                    lastValueFrom(stream$).then((response: any) => {
                        if (response) {
                            let reader: FileReader = new FileReader();

                            reader.readAsDataURL(response);
                            reader.onloadend = () => {
                                let photo = reader.result;
                                resolve(photo);
                            };
                        }
                    }).catch(err => {
                        console.error('[GraphService] getUserPhoto - Error fetching photo:', err);
                        reject(err);
                    });
                }
            }).catch(err => {
                console.error('[GraphService] getUserPhoto - Error acquiring graph token:', err);
                reject(err);
            });
        });

        return await p;
    }

    public async getUserGroups(role: any): Promise<any> {
        let results: any = null;

        try {
            const graphToken = await this.getGraphToken();

            let url = `https://graph.microsoft.com/v1.0/me/transitiveMemberOf/microsoft.graph.group?$count=true&$orderby=displayName&$filter=startswith(displayName, '${role}')`;
            let headers: HttpHeaders = new HttpHeaders({
                Authorization: `Bearer ${graphToken}`,
                ConsistencyLevel: "eventual"
            });

            const allGroups = await this.fetchAllGraphPages(url, headers);
            results = { value: allGroups };

        } catch (error: any) {
            console.error('[GraphService] getUserGroups - Error:', error.message);
            throw error;
        }

        return results;
    }

    public async checkUserInGroup(groupId: string): Promise<boolean> {
        try {
            const graphToken = await this.getGraphToken();

            let url = 'https://graph.microsoft.com/v1.0/me/memberOf';
            const headers: HttpHeaders = new HttpHeaders({
                Authorization: `Bearer ${graphToken}`
            });

            while (url) {
                const result: any = await lastValueFrom(this.http.get(url, { headers }));

                if (Array.isArray(result?.value) && result.value.some((group: any) => group.id === groupId)) {
                    return true;
                }

                url = result?.['@odata.nextLink'] || '';
            }

            return false;
        } catch (error: any) {
            console.error('[GraphService] checkUserInGroup - Error checking group membership:', error);

            // Only clear tokens and re-login for authentication errors (401) or token-related errors
            if (this.isAuthenticationError(error)) {
                this.clearTokensAndRelogin();
            }

            // Re-throw the error so the caller can handle it appropriately
            throw error;
        }
    }

    /**
     * Check if the error is an authentication/token error that requires re-login
     */
    private isAuthenticationError(error: any): boolean {
        // Check for HTTP 401 status
        if (error?.status === 401) {
            return true;
        }

        // Check for MSAL/Browser auth errors
        if (error?.errorCode === 'block_iframe_reload' ||
            error?.errorCode === 'interaction_required' ||
            error?.errorCode === 'login_required' ||
            error?.errorCode === 'token_expired' ||
            error?.name === 'BrowserAuthError' ||
            error?.name === 'InteractionRequiredAuthError') {
            return true;
        }

        // Check for error messages indicating token issues
        const errorMessage = error?.message?.toLowerCase() || '';
        if (errorMessage.includes('token') &&
            (errorMessage.includes('expired') || errorMessage.includes('invalid'))) {
            return true;
        }

        return false;
    }

    /**
     * Clear all token-related sessionStorage items and re-trigger MSAL login
     */
    private clearTokensAndRelogin(): void {
        // Clear all token-related sessionStorage items
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('access_token_expires_on');
        sessionStorage.removeItem('group_membership_verified');
        sessionStorage.removeItem('group_membership_group_id');

        // Trigger MSAL login redirect
        try {
            const clientId = this.config.getConfig('clientId');
            this.msalService.loginRedirect({
                scopes: [`${clientId}/.default`],
                redirectStartPage: window.location.pathname,
            });
        } catch (err) {
            console.error('[GraphService] Failed to trigger MSAL login redirect:', err);
        }
    }

    /**
     * Checks if the current user is a member of a specific AD group by object ID
     * @param requiredGroupObjectId The object ID of the AD group to check membership for
     * @returns Promise with { isMember: boolean }
     */
    public async isUserInRequiredGroup(requiredGroupObjectId: string): Promise<boolean> {
        try {
            const isMember = await this.checkUserInGroup(requiredGroupObjectId);

            if (isMember) {
                // console.log('[GraphService] isUserInRequiredGroup - User is member of required group:', requiredGroupObjectId);
            } else {
                // console.log('[GraphService] isUserInRequiredGroup - User is NOT member of required group:', requiredGroupObjectId);
            }

            return isMember;
        } catch (error: any) {
            console.error('[GraphService] isUserInRequiredGroup - Error checking group membership:', error.message);
            throw error;
        }
    }

    public async getUserProfile(): Promise<any> {
        try {
            const graphToken = await this.getGraphToken();
            const url = 'https://graph.microsoft.com/v1.0/me';
            const headers: HttpHeaders = new HttpHeaders({
                Authorization: `Bearer ${graphToken}`
            });
            const profile = await lastValueFrom(this.http.get(url, { headers }));
            return profile;
        } catch (err: any) {
            if (err?.errorCode === 'block_iframe_reload' || this.isRunningInIframe()) {
                console.warn('[GraphService] getUserProfile skipped in iframe auth context.');
                return null;
            }

            console.error('[GraphService] getUserProfile - Error:', err);
            throw err;
        }
    }

}